import type {
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
} from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { QueryClient } from "@citrine/client";

import { UserClient } from "../user";

import type { UserRequest } from "./user-request";

export class UserRequestClient extends QueryClient<UserRequest> {
  private static instance: UserRequestClient | undefined;

  clone: never;
  useClone: never;
  create: never;
  useCreate: never;
  delete: never;
  useDelete: never;
  update: never;
  useUpdate: never;
  upsert: never;
  useUpsert: never;

  protected constructor() {
    super({
      endpoint: "/api/v1/user-requests",
      getField: "data",
      findField: "data",
    });
  }

  static getInstance() {
    return (this.instance ??= new this());
  }

  useHasOutstandingRequests(options?: UseQueryOptions<UserRequest[]>) {
    const { is_admin } = UserClient.getInstance().useGetMe().data!;
    const requestsQuery = this.useFind(
      {},
      {
        ...options,
        enabled:
          // this request is only available to admins
          is_admin && options?.enabled !== false,
      },
    );
    return Boolean(requestsQuery.data?.length);
  }

  async reject(id: string): Promise<unknown> {
    try {
      const result = await this.request({
        method: "POST",
        url: `${this.getUri(id)}/reject`,
      });
      this.invalidateAfterMutation(id);
      return result;
    } catch {
      // do nothing
    }
  }

  useReject(
    options: UseMutationOptions<unknown, AxiosError, string> = {},
  ): UseMutationResult<unknown, AxiosError, string> {
    return useMutation({
      ...options,
      mutationFn: (id: string) => this.reject(id),
    });
  }

  async approve(id: string): Promise<unknown> {
    const result = await this.request({
      method: "POST",
      url: `${this.getUri(id)}/approve`,
    });
    this.invalidateAfterMutation(id);
    return result;
  }

  useApprove(options: UseMutationOptions<unknown, AxiosError, string> = {}) {
    return useMutation({ ...options, mutationFn: (id) => this.approve(id) });
  }
}
